import { HubOutlined, QueryStatsOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateRangeSelector } from "../../../../../components/UIToolkit/molecules/DateRangeSelector";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import SystemDialog from "../../../../../components/UIToolkit/Popups/Dialog";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../../../contexts/SnackbarContext";
import { FONT_SIZE, INK } from "../../../../../theme/tokens";
import { downloadBlob } from "../../../../../utils/downloadBlob";
import type { HrisIntegrationType } from "../../../../../utils/models";
import { useIntegrationEmployeesSummary, useIntegrationShiftsSummary, useIntegrationTimeOffSummary } from "../integrationSummaryHooks";
import { getSummaryErrorKey, SUMMARY_ERROR_PARAMS } from "./consts";
import { IntegrationSummaryContent } from "./IntegrationSummaryContent";
import { ShiftsSummaryContent } from "./ShiftsSummaryContent";
import { SUMMARY_DIALOG_CONFIG } from "./summaryDialogConfig";

type Props = {
	open: boolean;
	summaryType: HrisIntegrationType;
	onClose: () => void;
	onOpenMapping: () => void;
};

const XLSX_MEDIA_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const HrisIntegrationSummaryDialog = ({ open, summaryType, onClose, onOpenMapping }: Props) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const config = SUMMARY_DIALOG_CONFIG[summaryType];
	const { slug } = config;
	const defaultRange = useMemo(
		() => config.getDefaultRange({ timezone: chosenDepartment.timezone, dayStart: chosenDepartment.dayStart }),
		[config, chosenDepartment.timezone, chosenDepartment.dayStart],
	);
	const [startDate, setStartDate] = useState<DateTime>(defaultRange.start);
	const [endDate, setEndDate] = useState<DateTime>(defaultRange.end);
	const [isExporting, setIsExporting] = useState(false);

	useEffect(() => {
		if (!open) return;
		setStartDate(defaultRange.start);
		setEndDate(defaultRange.end);
	}, [open, defaultRange]);

	// Keep the hook call order stable when a different summary type opens. Only the selected query is
	// enabled, so the two inactive hooks register with React Query without making network requests.
	const employeesQuery = useIntegrationEmployeesSummary(chosenDepartment.department_id, startDate, endDate, open && summaryType === "employees");
	const timeOffQuery = useIntegrationTimeOffSummary(chosenDepartment.department_id, startDate, endDate, open && summaryType === "time_off");
	const shiftsQuery = useIntegrationShiftsSummary(chosenDepartment.department_id, startDate, endDate, open && summaryType === "shifts");
	const activeQuery = summaryType === "employees" ? employeesQuery : summaryType === "time_off" ? timeOffQuery : shiftsQuery;
	const summary = activeQuery.data;
	const errorKey = getSummaryErrorKey(activeQuery.error);
	const handleDownload = useCallback(async () => {
		if (!summary || isExporting) return;
		setIsExporting(true);
		track("hris_integration_summary_download_start", { summary_type: summaryType, department_id: chosenDepartment.department_id });
		try {
			const fileName = `hris-${slug}-summary-${startDate.toFormat("yyyy-MM-dd")}-to-${endDate.toFormat("yyyy-MM-dd")}`;
			const response = await config.download(chosenDepartment.department_id, startDate, endDate);
			downloadBlob(response.data, `${fileName}.xlsx`, XLSX_MEDIA_TYPE);
			track("hris_integration_summary_download_success", { summary_type: summaryType, department_id: chosenDepartment.department_id });
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : t("integration.summary.download.failed");
			trackError("hris_integration_summary_download_error", { summary_type: summaryType, error_message: errorMessage });
			showSnackbar(t("integration.summary.download.failed"), "error");
		} finally {
			setIsExporting(false);
		}
	}, [chosenDepartment.department_id, config, endDate, isExporting, showSnackbar, slug, startDate, summary, summaryType, t, track, trackError]);

	const handleMappingClick = useCallback(() => {
		track("hris_integration_summary_open_mapping", { summary_type: summaryType, department_id: chosenDepartment.department_id });
		onOpenMapping();
	}, [chosenDepartment.department_id, onOpenMapping, summaryType, track]);

	const statusBanner = summary ? (
		!summary.integrationConfigured ? (
			<SystemBanner
				tone="warning"
				icon={<QueryStatsOutlined />}
				title={t("integration.summary.notConfigured.title")}
				description={t("integration.summary.notConfigured.description")}
				dataTestId={`hris-${slug}-summary-not-configured`}
			/>
		) : (
			<InlineHint tone="neutral" icon={<QueryStatsOutlined />} dataTestId={`hris-${slug}-summary-sync-status`}>
				{summary.hasEverSynced
					? summary.lastSyncAt
						? t("integration.summary.lastSync", {
								date: summary.lastSyncAt.setZone(chosenDepartment.timezone).toLocaleString(DateTime.DATETIME_MED),
							})
						: t("integration.summary.lastSyncUnknown")
					: t("integration.summary.notRunYet.status")}
			</InlineHint>
		)
	) : null;
	const summaryContent =
		summaryType === "employees" && employeesQuery.data ? (
			<IntegrationSummaryContent summaryType="employees" summary={employeesQuery.data} />
		) : summaryType === "time_off" && timeOffQuery.data ? (
			<IntegrationSummaryContent summaryType="time_off" summary={timeOffQuery.data} />
		) : summaryType === "shifts" && shiftsQuery.data ? (
			<ShiftsSummaryContent summary={shiftsQuery.data} />
		) : null;

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t(config.titleKey)}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }} data-testid={`hris-${slug}-summary-dialog-body`}>
					{config.showMappingBanner && (
						<SystemBanner
							tone="info"
							icon={<HubOutlined />}
							title={t("integration.summary.mapping.title")}
							description={t("integration.summary.mapping.description")}
							action={
								<SystemButton buttonVariant="borderless" size="small" onClick={handleMappingClick} data-testid="hris-summary-open-mapping-button">
									{t("integration.summary.mapping.action")}
								</SystemButton>
							}
							dataTestId={`hris-${slug}-summary-mapping-banner`}
						/>
					)}
					<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} data-testid={`hris-${slug}-summary-date-filter`}>
						<Typography component="h2" sx={{ color: INK.SECONDARY, fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700 }}>
							{t(config.dateFilterTitleKey)}
						</Typography>
						<DateRangeSelector
							startDate={startDate}
							endDate={endDate}
							onStartDateChange={setStartDate}
							onEndDateChange={setEndDate}
							label={t("integration.summary.dateFilter.label")}
							startDateTestId={`hris-${slug}-summary-start-date`}
							endDateTestId={`hris-${slug}-summary-end-date`}
							containerSX={{ backgroundColor: "transparent", border: "none", padding: 0 }}
						/>
					</Box>
					{activeQuery.isLoading && (
						<InlineHint tone="info" icon={<QueryStatsOutlined />} dataTestId={`hris-${slug}-summary-loading`}>
							{t("integration.summary.loading")}
						</InlineHint>
					)}
					{activeQuery.isError && (
						<SystemBanner
							tone="error"
							title={t(config.loadErrorTitleKey)}
							// DEFAULT: an unknown/catch-all failure uses the shared retry advice; a specific
							// backend reason (e.g. the range is too long) instead says what to change.
							description={errorKey && errorKey !== config.loadFailedKey ? t(errorKey, SUMMARY_ERROR_PARAMS) : t("integration.summary.loadError.description")}
							dataTestId={`hris-${slug}-summary-error`}
						/>
					)}
					{statusBanner}
					{summaryContent}
				</Box>
			}
			cancel={t("close")}
			confirm={t("integration.summary.download.xlsx")}
			onConfirm={handleDownload}
			confirmDisabled={!summary || activeQuery.isLoading || activeQuery.isError || isExporting}
			confirmLoading={isExporting}
			confirmButtonVariant="primary"
			maxWidth="lg"
			fullWidth
			dataTestId={`hris-${slug}-summary-dialog`}
		/>
	);
};
